import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { trackEvent } from "@/utils/analytics";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import FAQSchema from "@/components/FAQSchema";
import ContactModal from "@/components/new-landing/ContactModal";
import TrustBar from "@/components/TrustBar";

const NAVY = "#002178";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const assessmentTypes = [
  {
    number: "01",
    name: "Product Strategy & Market",
    bullets: [
      "Market positioning and competitive landscape",
      "ICP alignment and segmentation",
      "Product-market fit signals",
      "Pricing and packaging analysis",
    ],
    result:
      "Strategic clarity on where to compete and how to win",
  },
  {
    number: "02",
    name: "Product Growth & User Behavior",
    bullets: [
      "Activation and onboarding funnels",
      "Conversion and retention metrics",
      "User journey analysis",
      "Growth experiment prioritization",
    ],
    result:
      "A ranked list of growth levers with expected impact",
  },
  {
    number: "03",
    name: "Product Talent",
    bullets: [
      "Team structure and role coverage",
      "PM competency assessment",
      "Leadership gaps and succession",
      "Hiring roadmap",
    ],
    result:
      "Talent plan aligned to your growth targets",
  },
  {
    number: "04",
    name: "Product Operations",
    bullets: [
      "Development lifecycle and velocity",
      "Roadmap process and prioritization",
      "Cross-functional alignment",
      "Technical debt assessment",
    ],
    result:
      "Execution framework that ships faster without burning out",
  },
  {
    number: "05",
    name: "Product Marketing (PMM)",
    bullets: [
      "Go-to-market alignment",
      "Messaging and positioning",
      "Launch process and effectiveness",
      "Sales enablement gaps",
    ],
    result:
      "GTM engine that converts product value into revenue",
  },
];

const steps = [
  {
    number: "01",
    title: "Week 1-2: Deep dive",
    description:
      "We embed with your team, analyze data, interview stakeholders, audit processes. We've run this process across a decade of engagements. We know where to look.",
  },
  {
    number: "02",
    title: "Week 3: The map",
    description:
      "You get a clear, prioritized action plan. The 5% that matters most, ranked by impact and effort.",
  },
  {
    number: "03",
    title: "Week 4+: Execute",
    description:
      "We can stay to lead execution, or hand off to your team with full documentation.",
  },
];

const faqs = [
  {
    question: "What is a technology company product assessment?",
    answer: "A technology company product assessment is a structured evaluation of your product organization across five dimensions: strategy and market, growth and user behavior, talent, operations, and product marketing. You get a prioritized action plan showing exactly what to fix and why, ranked by business impact."
  },
  {
    question: "How is this different from a product audit?",
    answer: "Most product audits review what exists. Our assessment identifies the 5% of work that will drive outsized results. We don't just tell you what's broken. We map the fastest path to measurable business impact."
  },
  {
    question: "How long does a product assessment take?",
    answer: "Focused assessments run as fast as 2-4 weeks. Comprehensive multi-area assessments take 4-12 weeks. Either way, you get actionable insights faster than any traditional consulting engagement."
  },
  {
    question: "Can you assess multiple portfolio companies?",
    answer: "Yes. PE firms often engage us to assess product across their entire portfolio. We customize the approach per company but maintain consistency in methodology, so you can compare product maturity across investments."
  },
  {
    question: "What happens after the assessment?",
    answer: "You get a prioritized action plan. Most clients ask us to stay and lead execution on the highest-impact items. We can also hand off to your team with full documentation and coaching."
  }
];

const Assessment = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <PageLayout>
      <SEO
        title="Product Assessment | The 5% Methodology"
        description="We find the 5% of product work that drives all the impact. Five assessment types covering strategy, growth, talent, operations, and product marketing."
        path="/assessment"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "dateModified": "2026-03-24"
            },
            {
              "@type": "HowTo",
              "name": "How a Product Assessment Works",
              "description": "A structured, time-boxed process built from dozens of assessments across PE-backed B2B technology companies. Find the 5% of product work that drives outsized business impact.",
              "step": [
                {
                  "@type": "HowToStep",
                  "position": 1,
                  "name": "Deep Dive",
                  "text": "We embed with your team, analyze data, interview stakeholders, and audit processes. We've run this process across a decade of engagements. We know where to look.",
                  "url": "https://fractionalcpo.com/assessment#how-it-works"
                },
                {
                  "@type": "HowToStep",
                  "position": 2,
                  "name": "The Map",
                  "text": "You get a clear, prioritized action plan. The 5% that matters most, ranked by impact and effort.",
                  "url": "https://fractionalcpo.com/assessment#how-it-works"
                },
                {
                  "@type": "HowToStep",
                  "position": 3,
                  "name": "Execute",
                  "text": "We can stay to lead execution, or hand off to your team with full documentation.",
                  "url": "https://fractionalcpo.com/assessment#how-it-works"
                }
              ],
              "totalTime": "P4W"
            }
          ]
        }}
      />

      {/* Section 1: Hero */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-8 font-medium"
          >
            Product Assessment
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[clamp(3rem,10vw,8rem)] font-light leading-[0.9] tracking-[-0.03em] mb-8"
          >
            The <span className="text-[#002178] font-medium">5%</span>
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-[clamp(1.25rem,3vw,1.75rem)] font-light text-black/70 leading-[1.4] mb-8 max-w-2xl"
          >
            Most product work doesn't move the needle. We find the 5% that does.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-2xl mb-12"
          >
            <p className="text-lg text-black/60 leading-relaxed">
              In weeks, our team audits your product growth levers, uncovering
              the small set of changes that drive outsized business impact. No
              slides. No 90-day discovery. Just a clear map of what to fix and
              why.
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <button
              onClick={() => { trackEvent('contact_click', { source: 'hero' }); setIsContactOpen(true); }}
              className="px-8 py-4 bg-[#002178] text-white font-medium hover:scale-105 transition-transform"
            >
              Let's chat
            </button>
            <TrustBar />
          </motion.div>
        </div>
      </section>

      {/* Section 2: Five Assessment Types */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <motion.h2
            {...fadeUp}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-16"
          >
            Five lenses.{" "}
            <span className="text-[#002178] font-medium">One diagnosis.</span>
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assessmentTypes.map((type, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.1 }}
                className="group border border-black/10 bg-white p-8 hover:border-[#002178]/30 hover:shadow-lg transition-all duration-300 flex flex-col"
              >
                <span className="text-[3rem] font-light text-[#002178]/20 leading-none block mb-4">
                  {type.number}
                </span>
                <h3 className="text-xl font-medium mb-4 group-hover:text-[#002178] transition-colors">
                  {type.name}
                </h3>
                <ul className="space-y-2 mb-6 flex-1">
                  {type.bullets.map((bullet, j) => (
                    <li
                      key={j}
                      className="text-black/50 text-sm leading-relaxed flex items-start gap-2"
                    >
                      <span className="w-1 h-1 rounded-full bg-[#002178]/30 flex-shrink-0 mt-[0.45rem]" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 border-t border-black/10 -mx-8 px-8">
                  <p className="text-sm font-medium text-[#002178]/80">
                    {type.result}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: How It Works */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div className="md:sticky md:top-32">
              <motion.h2
                {...fadeUp}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-8"
              >
                How it works
              </motion.h2>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-white/60 leading-relaxed mb-8"
              >
                A structured, time-boxed process built from dozens of
                assessments across PE-backed B2B technology companies.
              </motion.p>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-white/60"
              >
                <Link
                  to="/case-study"
                  className="text-white underline underline-offset-4 hover:text-white/80 transition-colors"
                >
                  See what happens after an assessment &rarr;
                </Link>
              </motion.p>
            </div>

            <div className="space-y-0">
              {steps.map((step, i) => (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ delay: i * 0.1 }}
                  className="group py-8 border-b border-white/10"
                >
                  <div className="flex gap-6 items-baseline">
                    <span className="text-white/20 font-mono text-sm">
                      {step.number}
                    </span>
                    <div>
                      <h3 className="text-xl md:text-2xl font-medium mb-2 text-white group-hover:text-white/80 transition-colors">
                        {step.title}
                      </h3>
                      <p className="text-white/50 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: FAQ */}
      <div className="bg-[#F5F5F0]">
        <FAQSchema faqs={faqs} heading="Frequently Asked Questions" />
      </div>

      {/* Section 5: Bottom CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto text-center">
          <motion.h2
            {...fadeUp}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-6"
          >
            Ready to Find Your 5%?
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg text-black/60 leading-relaxed mb-12 max-w-xl mx-auto"
          >
            Every engagement starts with understanding where you're stuck and what's possible. In weeks, you'll have a clear map of what to fix and why.
          </motion.p>
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <button
              onClick={() => { trackEvent('contact_click', { source: 'cta_block' }); setIsContactOpen(true); }}
              className="inline-block px-8 py-4 bg-[#002178] text-white font-medium hover:scale-105 transition-transform"
            >
              Let's chat
            </button>
          </motion.div>
        </div>
      </section>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </PageLayout>
  );
};

export default Assessment;
